// Start klienta: načte konfiguraci stránky, zkusí relaci (sessionStorage) nebo zapamatované zařízení (cookie),
// jinak ukáže přihlášení. Po přihlášení stáhne data relace a postaví plochu.
import './apps.generated';
import './views/logview'; // okno Logy (jádro LogWindow)
import { api, configureApi, getToken, setToken, onAuthLost, onLocked, onElevationRequired, ApiError } from './api';
import { configureAi } from './components/ai';
import type { PageBoot, SessionBoot, UserInfo } from './contract';
import { setTexts, t } from './i18n';
import { events } from './events';
import { Desktop } from './shell/desktop';
import { loginScreen, forgetAccount } from './shell/login';
import { applyBranding } from './theme';
import { toast } from './components/toast';

declare const __IWAN_VERSION__: string;

const bootEl = document.getElementById('iwan-boot');
const page: PageBoot = JSON.parse(bootEl?.textContent ?? '{}') as PageBoot;
const container = document.getElementById('app') as HTMLElement;
configureApi(page.prefix ?? '');
applyBranding(page.branding ?? {});

let desktop: Desktop | null = null;
let relogging = false;

async function loadUiTexts(lang: string): Promise<void> {
  // texty pro přihlášení (před relací): základní jazyk ze stránky
  try {
    const res = await fetch(`${page.prefix}/assets/lang/${lang}.json?v=${page.assetHash}`);
    if (res.ok) {
      setTexts((await res.json()) as Record<string, string | Record<string, string>>, lang);
      return;
    }
  } catch {
    /* ignore */
  }
  setTexts({}, lang);
}

async function start(): Promise<void> {
  const lang = navigator.language.toLowerCase().startsWith('en') && page.langs.includes('en') ? 'en' : page.defaultLang;
  await loadUiTexts(lang);
  if (!getToken()) {
    try {
      const dev = await api<{ token: string | null; user?: UserInfo }>('/auth/device', {});
      if (dev.token) setToken(dev.token);
    } catch {
      /* bez zapamatovaného zařízení */
    }
  }
  if (!getToken()) {
    const res = await loginScreen(page, container);
    void res;
  }
  await enterDesktop();
}

async function enterDesktop(): Promise<void> {
  let boot: SessionBoot;
  try {
    boot = await api<SessionBoot>('/session');
  } catch (e) {
    setToken(null);
    const err = e as ApiError;
    const res = await loginScreen(page, container, { message: err.status === 401 ? undefined : err.message });
    void res;
    return enterDesktop();
  }
  setTexts(boot.texts, boot.lang);
  applyBranding(boot.branding ?? {});
  desktop?.destroy();
  desktop = new Desktop(boot, page, container, {
    logout: async (forget) => {
      await api('/auth/logout', { forgetDevice: forget }).catch(() => undefined);
      if (forget) forgetAccount(boot.user.login);
      leave();
    },
    switchUser: () => {
      void api('/auth/logout', {}).catch(() => undefined);
      leave(true);
    },
  });
  configureAi(boot.ai, boot.prefs);
  events.setCursor(boot.eventsCursor);
  events.start(boot.eventsCursor);
  events.onSessionLost = () => void relogin();
  onAuthLost(() => void relogin());
  onLocked(() => void desktop?.lock(false));
  onElevationRequired(() => desktop ? desktop.elevate() : Promise.resolve(false));
  document.title = `${page.system.name} · ${boot.customer.name}`;
  console.info(`${page.system.name} ${page.system.version} (client ${__IWAN_VERSION__})`);
}

async function relogin(): Promise<void> {
  if (relogging || !desktop) return;
  relogging = true;
  try {
    await desktop.relogin();
  } finally {
    relogging = false;
  }
}

function leave(switching = false): void {
  events.stop();
  setToken(null);
  desktop?.destroy();
  desktop = null;
  void loginScreen(page, container, { switching }).then(() => enterDesktop());
}

start().catch((e) => {
  console.error(e);
  toast(t('core.err.internal'), 'error');
});
